import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import Button from '../ui/Button';

/**
 * Marketing Navigation Header.
 * Landing page uses approved compact nav treatment.
 */
const MarketingNav = ({ transparent = false }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [professionalsMenuOpen, setProfessionalsMenuOpen] = useState(false);
  const [mobileProfessionalsOpen, setMobileProfessionalsOpen] = useState(false);
  const professionalsCloseTimerRef = useRef(null);
  const location = useLocation();

  const clearProfessionalsCloseTimer = () => {
    if (professionalsCloseTimerRef.current) {
      clearTimeout(professionalsCloseTimerRef.current);
      professionalsCloseTimerRef.current = null;
    }
  };

  const openProfessionalsMenu = () => {
    clearProfessionalsCloseTimer();
    setProfessionalsMenuOpen(true);
  };

  const queueProfessionalsMenuClose = () => {
    clearProfessionalsCloseTimer();
    professionalsCloseTimerRef.current = setTimeout(() => {
      setProfessionalsMenuOpen(false);
    }, 160);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setProfessionalsMenuOpen(false);
    setMobileProfessionalsOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    return () => {
      clearProfessionalsCloseTimer();
    };
  }, []);

  const navLinks = [
    { name: 'How It Works', path: '/how-it-works' },
    { name: 'Benefits', path: '/benefits' },
    { name: 'The Journey', path: '/journey' },
    { name: 'Research References', path: '/research-references' },
  ];

  const isLanding = location.pathname === '/';
  const desktopLandingLinks = [
    { name: 'For clinicians', path: '/clinicians' },
    { name: 'Sign in', path: '/login' },
  ];

  const professionalLinks = [
    { name: 'Licensed Clinicians', path: '/clinicians' },
    { name: 'Clergy', path: '/clinicians?audience=clergy' },
    { name: 'Coaches', path: '/clinicians?audience=coach' },
    { name: 'Enterprise Programs', path: '/clinicians?audience=enterprise' },
  ];

  const isTransparent = transparent && !isScrolled;
  
  const navClasses = `fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
    isTransparent 
      ? 'bg-transparent' 
      : 'bg-white/95 backdrop-blur-md shadow-sm border-b border-neutral-100'
  }`;

  const textClasses = isTransparent ? 'text-white' : 'text-neutral-700';
  const hoverClasses = isTransparent 
    ? 'hover:text-white/80' 
    : 'hover:text-primary';

  return (
    <>
      <nav className={navClasses}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" style={{ maxWidth: '1280px' }}>
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link to="/" className="flex items-center gap-3">
              {isLanding ? (
                <div className={`leading-none ${textClasses}`}>
                  <p className="text-[10px] tracking-[0.2em] font-bold uppercase">THE MHDF</p>
                  <p className="text-xl md:text-2xl font-extrabold">Momentum</p>
                </div>
              ) : (
                <img
                  src="/logo with words.png"
                  alt="MHDF Momentum"
                  className="h-12 md:h-14 w-auto"
                />
              )}
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {isLanding ? (
                <>
                  {desktopLandingLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className={`text-sm font-medium transition-colors ${textClasses} ${hoverClasses}`}
                    >
                      {link.name}
                    </Link>
                  ))}
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() => { window.location.href = '/register'; }}
                    className="!rounded-xl !font-bold"
                    style={{ backgroundColor: 'var(--gold-accent)', color: 'var(--gold-accent-text)' }}
                  >
                    Start day 1
                  </Button>
                </>
              ) : (
                <>
                  {navLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className={`text-sm font-medium transition-colors ${
                        location.pathname === link.path
                          ? 'text-primary font-semibold'
                          : `${textClasses} ${hoverClasses}`
                      }`}
                    >
                      {link.name}
                    </Link>
                  ))}

                  <div
                    className="relative"
                    onMouseEnter={openProfessionalsMenu}
                    onMouseLeave={queueProfessionalsMenuClose}
                  >
                    <button
                      type="button"
                      onClick={() => setProfessionalsMenuOpen((prev) => !prev)}
                      className={`inline-flex items-center gap-1 text-sm font-medium transition-colors ${textClasses} ${hoverClasses}`}
                    >
                      Guided Professionals
                      <ChevronDown size={16} className={`transition-transform ${professionalsMenuOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {professionalsMenuOpen && (
                      <div
                        className="absolute right-0 top-full pt-2 w-64"
                        onMouseEnter={openProfessionalsMenu}
                        onMouseLeave={queueProfessionalsMenuClose}
                      >
                        <div className="rounded-lg border border-neutral-200 bg-white shadow-lg py-2">
                        {professionalLinks.map((link) => (
                          <Link
                            key={link.name}
                            to={link.path}
                            className="block px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50"
                            onClick={() => setProfessionalsMenuOpen(false)}
                          >
                            {link.name}
                          </Link>
                        ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <Link to="/login">
                    <Button
                      size="sm"
                      variant="ghost"
                      className={textClasses}
                    >
                      Sign In
                    </Button>
                  </Link>

                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() => window.location.href = '/register'}
                  >
                    Begin Your 365 Days
                  </Button>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              className={`md:hidden p-2 rounded-md ${textClasses} ${hoverClasses} transition-colors`}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle mobile menu"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-dark/50 backdrop-blur-sm"
            onClick={() => setMobileMenuOpen(false)}
          />
          
          {/* Menu Panel */}
          <div className="absolute top-16 right-0 bottom-0 w-64 bg-white shadow-xl">
            <div className="flex flex-col p-4 space-y-4">
              {(isLanding ? desktopLandingLinks : navLinks).map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-base font-medium py-2 px-4 rounded-md transition-colors ${
                    location.pathname === link.path
                      ? 'bg-primary/10 text-primary font-semibold'
                      : 'text-neutral-700 hover:bg-neutral-50'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.name}
                </Link>
              ))}

              {!isLanding && (
                <div className="rounded-md border border-neutral-200">
                  <button
                    type="button"
                    className="w-full flex items-center justify-between px-4 py-2 text-left text-base font-medium text-neutral-700"
                    onClick={() => setMobileProfessionalsOpen((prev) => !prev)}
                  >
                    Guided Professionals
                    <ChevronDown size={16} className={`transition-transform ${mobileProfessionalsOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {mobileProfessionalsOpen && (
                    <div className="pb-2">
                      {professionalLinks.map((link) => (
                        <Link
                          key={link.name}
                          to={link.path}
                          className="block px-6 py-2 text-sm text-neutral-600 hover:bg-neutral-50"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {link.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {/* On This Page — only on the landing page */}
              {location.pathname === '/' && (
                <div className="pt-4 border-t border-neutral-200">
                  <p className="px-4 pb-1 text-[10px] font-bold uppercase tracking-widest text-neutral-400">
                    On This Page
                  </p>
                  {[
                    { label: 'The Challenge', anchor: 'challenges' },
                    { label: 'Daily Practice', anchor: 'pillars' },
                    { label: 'The 4 Phases', anchor: 'phases' },
                    { label: 'What It Is & Isn\'t', anchor: 'what-it-is' },
                    { label: 'For Clinicians', anchor: 'for-clinicians' },
                    { label: 'Get Started', anchor: 'get-started' },
                  ].map(({ label, anchor }) => (
                    <a
                      key={anchor}
                      href={`#${anchor}`}
                      className="block px-4 py-2 text-sm text-neutral-600 hover:bg-neutral-50 hover:text-primary rounded-md"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {label}
                    </a>
                  ))}
                </div>
              )}

              <div className="pt-4 border-t border-neutral-200">
                <Button 
                  fullWidth 
                  size="md" 
                  variant="primary"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    window.location.href = '/register';
                  }}
                >
                  {isLanding ? 'Start day 1' : 'Begin Your 365 Days'}
                </Button>
                
                <Button 
                  fullWidth 
                  size="md" 
                  variant="ghost"
                  className="mt-2"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    window.location.href = '/login';
                  }}
                >
                  Sign In
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MarketingNav;
